import { useState, useEffect, useRef } from 'react';
import { cloneDeep, has, isEmpty, isNil } from 'lodash';
// Import Types
import type { DirectionType } from 'types';
// Import Hooks
import useQueryParams from './UseQueryParams';

interface Result {
    data: {
        pageSize: number;
        pageNumber: number;
        sortingName: string;
        sortingDirection: DirectionType;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setData: (tmpData: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    changeDefaultSorting: (tmpData: any) => void;
    queryParams?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        encode: (data: any) => string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        decode: () => any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        historyReplace: (data: any) => void;
    };
}

const useDataTable = (
    defaultPageNumber,
    defaultPageSize,
    defaultSortingName,
    defaultSortingDirection,
    withQueryParams = false,
    queryNamespace = '',
) => {
    const defaultSorting = useRef({ Name: defaultSortingName, Direction: defaultSortingDirection });

    const queryParams = useQueryParams(queryNamespace);

    const getDefaultData = () => {
        const result = {
            pageSize: defaultPageSize,
            pageNumber: defaultPageNumber,
            sortingName: defaultSortingName,
            sortingDirection: defaultSortingDirection,
        };
        if (withQueryParams && !isNil(queryNamespace) && !isEmpty(queryNamespace)) {
            const decodedParams = queryParams.decode();
            if (!isNil(decodedParams?.pageNumber)) {
                result.pageNumber = decodedParams.pageNumber;
            }
            if (!isNil(decodedParams?.pageSize)) {
                result.pageSize = decodedParams.pageSize;
            }
            if (!isNil(decodedParams?.sortingName)) {
                result.sortingName = decodedParams.sortingName;
            }
            if (!isNil(decodedParams?.sortingDirection)) {
                result.sortingDirection = decodedParams.sortingDirection;
            }
        }

        return result;
    };

    const [data, setData] = useState(getDefaultData());

    const changeDefaultSorting = (tmpData) => {
        if (!isNil(tmpData?.sortingName)) {
            defaultSorting.current.Name = tmpData.sortingName;
        }
        if (!isNil(tmpData?.sortingDirection)) {
            defaultSorting.current.Direction = tmpData.sortingDirection;
        }
    };

    const changeData = (tmpData) => {
        const newData = cloneDeep(data);

        if (!isNil(tmpData?.pageNumber)) {
            newData.pageNumber = tmpData.pageNumber;
        }
        if (!isNil(tmpData?.pageSize)) {
            newData.pageSize = tmpData.pageSize;
        }
        if (has(tmpData, 'sortingName')) {
            newData.sortingName = !isNil(tmpData?.sortingName) ? tmpData.sortingName : defaultSorting.current.Name;
        }
        if (has(tmpData, 'sortingDirection')) {
            if (!isNil(tmpData?.sortingDirection)) {
                newData.sortingDirection = tmpData.sortingDirection;
            } else {
                newData.sortingDirection = defaultSorting.current.Direction;
                newData.sortingName = defaultSorting.current.Name;
            }
        }

        if (withQueryParams) {
            queryParams.historyReplace(newData);
        }

        setData(newData);
    };

    useEffect(() => {
        if (withQueryParams) {
            queryParams.historyReplace(data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const result: Result = { data, setData: changeData, changeDefaultSorting };

    if (withQueryParams) {
        result.queryParams = queryParams;
    }

    return result;
};

export default useDataTable;
